import { useEffect, useState, useContext } from "react";
import { TrackingContext } from "Contexts/TrackingContext/TrackingContext";
import validateSegmentTrackEvent from "Hooks/useSegment/segmentValidators";
import { sanitize } from "Utilities/ObjectUtilities";
import { validateSegmentEvent } from "../../Utilities/Instrumentation/SegmentUtilities/segmentFunctions";

const useSegment = () => {
  const [trackEventQueue, updateTrackEventQueue] = useState([]);
  const [pageEventQueue, updatePageEventQueue] = useState([]);
  const [identifyEventQueue, updateIdentifyEventQueue] = useState([]);

  const trackingContext = useContext(TrackingContext);
  const segmentReady = trackingContext.segmentReady;

  //track events
  useEffect(() => {
    if (segmentReady && trackEventQueue.length) {
      trackEventQueue.forEach((event) => {
        try {
          // Remove properties that are null or undefined
          const sanitizedEvent = sanitize(event);
          const name = sanitizedEvent.name;
          const customEventName = sanitizedEvent.event;
          const properties = sanitizedEvent.properties;

          validateSegmentEvent(name || customEventName, properties);

          if (name) {
            analytics.track(name, properties);
          } else if (customEventName) {
            analytics.track(customEventName, properties);
          } else {
            analytics.track(properties);
          }
        } catch (err) {
          console.error(err);
        }
      });
      updateTrackEventQueue([]);
    }
  }, [segmentReady, JSON.stringify(trackEventQueue)]);

  //page events
  useEffect(() => {
    if (segmentReady && pageEventQueue.length) {
      pageEventQueue.forEach((event) => {
        try {
          analytics.page(event);
        } catch (err) {
          console.error(err);
        }
      });
      updatePageEventQueue([]);
    }
  }, [segmentReady, JSON.stringify(pageEventQueue)]);

  //identify events
  useEffect(() => {
    if (segmentReady && identifyEventQueue.length) {
      identifyEventQueue.forEach((event) => {
        try {
          analytics.identify(event);
        } catch (err) {
          console.error(err);
        }
      });
      updateIdentifyEventQueue([]);
    }
  }, [segmentReady, JSON.stringify(identifyEventQueue)]);

  const sendSegmentTrackEvent = (eventProperties) => {
    let event = {
      properties: eventProperties,
    };

    //if segment is ready, don't even put it in the queue, just gonna send it
    if (segmentReady) {
      try {
        analytics.track(eventProperties);
      } catch (err) {
        console.error(err);
      }
    } else {
      updateTrackEventQueue((prevItems) => [...prevItems, { ...event }]);
    }
  };

  const sendSegmentTrackEventWithName = (eventName, eventProperties) => {
    let event = {
      name: eventName,
      properties: eventProperties,
    };

    validateSegmentTrackEvent(event);
    updateTrackEventQueue((prevItems) => [...prevItems, { ...event }]);
  };

  const sendCustomSegmentTrackEvent = (event) => {
    updateTrackEventQueue((prevItems) => [...prevItems, { ...event }]);
  };

  const sendSegmentPageEvent = (event) => {
    updatePageEventQueue((prevItems) => [...prevItems, { ...event }]);
  };

  const sendSegmentIdentifyEvent = (event) => {
    updateIdentifyEventQueue((prevItems) => [...prevItems, { ...event }]);
  };

  return {
    sendSegmentTrackEvent,
    sendSegmentTrackEventWithName,
    sendSegmentPageEvent,
    sendSegmentIdentifyEvent,
    sendCustomSegmentTrackEvent,
  };
};

export default useSegment;
